import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video, Youtube, Instagram, Globe, X, Music, QrCode,
  Search, ArrowRightLeft, ChevronRight, BookOpen, MessageSquare,
  Package, Lightbulb, Mic, Captions,
  Home, LogOut, Clock, Coins,
} from 'lucide-react';

export type Tool =
  | 'home'
  | 'video' | 'youtube' | 'instagram'
  | 'dev-tools' | 'audio-pro' | 'qr-studio' | 'ig-audit' | 'ig-compare'
  | 'ai-blog' | 'ai-captions' | 'ai-product' | 'ai-ideas'
  | 'ai-transcribe' | 'video-subtitles'
  | 'web-intel' | 'website-report' | 'usage-report';

interface SidebarProps {
  activeTool: Tool;
  onSelectTool: (tool: Tool) => void;
  isOpen: boolean;
  onClose: () => void;
  user: { id: string; username: string; role: string };
  onLogout: () => Promise<void>;
  onHistoryOpen: () => void;
}

// Grouped by team. Core, most-used tools come first within each group;
// rarely-used utilities live in the collapsible "Extras" group at the bottom.
const categories: { title: string; collapsible?: boolean; items: { id: Tool; label: string; icon: React.ElementType; color: string; glow: string }[] }[] = [
  {
    title: 'Video Team',
    items: [
      { id: 'ai-transcribe',   label: 'Transcribe',      icon: Mic,       color: '#216974', glow: 'rgba(33,105,116,0.25)'  },
      { id: 'video-subtitles', label: 'Video Subtitles', icon: Captions,  color: '#54c4b6', glow: 'rgba(84,196,182,0.25)'  },
      { id: 'video',           label: 'Video & Media',   icon: Video,     color: '#e4c184', glow: 'rgba(228,193,132,0.25)' },
      { id: 'audio-pro',       label: 'Audio Pro',       icon: Music,     color: '#216974', glow: 'rgba(33,105,116,0.25)'  },
      { id: 'youtube',         label: 'YouTube DL',      icon: Youtube,   color: '#54c4b6', glow: 'rgba(84,196,182,0.25)'  },
      { id: 'instagram',       label: 'Instagram DL',    icon: Instagram, color: '#e4c184', glow: 'rgba(228,193,132,0.25)' },
    ],
  },
  {
    title: 'Social Media Team',
    items: [
      { id: 'ai-ideas',    label: 'Content Calendar',  icon: Lightbulb,      color: '#54c4b6', glow: 'rgba(84,196,182,0.25)'  },
      { id: 'ai-captions', label: 'Caption Generator', icon: MessageSquare,  color: '#216974', glow: 'rgba(33,105,116,0.25)'  },
      { id: 'ig-audit',    label: 'IG Audit',          icon: Search,         color: '#e4c184', glow: 'rgba(228,193,132,0.25)' },
      { id: 'ig-compare',  label: 'IG Compare',        icon: ArrowRightLeft, color: '#54c4b6', glow: 'rgba(84,196,182,0.25)'  },
      { id: 'ai-product',  label: 'Product Copy',      icon: Package,        color: '#216974', glow: 'rgba(33,105,116,0.25)'  },
    ],
  },
  {
    title: 'Web & SEO Team',
    items: [
      { id: 'website-report', label: 'Website Report',    icon: Globe,    color: '#e4c184', glow: 'rgba(228,193,132,0.25)' },
      { id: 'web-intel',      label: 'Site Intelligence', icon: Search,   color: '#216974', glow: 'rgba(33,105,116,0.25)'  },
      { id: 'ai-blog',        label: 'Blog Writer',       icon: BookOpen, color: '#54c4b6', glow: 'rgba(84,196,182,0.25)'  },
    ],
  },
  {
    title: 'Extras',
    collapsible: true,
    items: [
      { id: 'qr-studio', label: 'QR Studio',       icon: QrCode,   color: '#e4c184', glow: 'rgba(228,193,132,0.25)' },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ activeTool, onSelectTool, isOpen, onClose, user, onLogout, onHistoryOpen }) => {
  const [extrasOpen, setExtrasOpen] = useState(false);

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[272px] sidebar-glass flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="px-5 pt-6 pb-5 border-b border-white/[0.06] flex items-center justify-between">
          <button
            onClick={() => { onSelectTool('home'); if (window.innerWidth < 1024) onClose(); }}
            className="flex items-center gap-3 group"
          >
            <div className="relative w-9 h-9 rounded-xl overflow-hidden shrink-0">
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #54c4b6 0%, #216974 100%)' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-black text-base leading-none select-none">aN</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-black text-white leading-tight tracking-tight">
                <span style={{ color: '#54c4b6' }}>aadi</span>
                <span style={{ color: '#e4c184' }}>N</span>
              </p>
              <p className="text-[10px] text-white/35 font-medium tracking-widest uppercase mt-0.5">
                The beginning of unlimited
              </p>
            </div>
          </button>
          <button
            onClick={onClose}
            className="lg:hidden w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/50 hover:text-white/80"
          >
            <X size={16} />
          </button>
        </div>

        {/* Home button */}
        <div className="px-3 pt-3 pb-1">
          <motion.button
            onClick={() => { onSelectTool('home'); if (window.innerWidth < 1024) onClose(); }}
            whileTap={{ scale: 0.98 }}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
              transition-all duration-200 group relative overflow-hidden
              ${activeTool === 'home' ? 'text-white' : 'text-white/45 hover:text-white/80'}
            `}
            style={activeTool === 'home' ? {
              background: 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(255,255,255,0.06) 100%)',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 2px 8px rgba(124,58,237,0.25)',
            } : undefined}
          >
            {activeTool !== 'home' && (
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.04] transition-colors duration-200" />
            )}
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200"
              style={{
                backgroundColor: activeTool === 'home' ? '#7c3aed22' : 'rgba(255,255,255,0.05)',
                color: activeTool === 'home' ? '#a78bfa' : 'inherit',
              }}
            >
              <Home size={15} strokeWidth={activeTool === 'home' ? 2.2 : 1.8} />
            </div>
            <span className="text-[13px] font-medium flex-1 text-left leading-none">Home</span>
            {activeTool === 'home' && <ChevronRight size={12} className="text-white/40 shrink-0" />}
          </motion.button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-5">
          {categories.map((category) => {
            // Keep Extras open while one of its tools is active
            const expanded = !category.collapsible || extrasOpen || category.items.some(t => t.id === activeTool);
            return (
            <div key={category.title} className={category.collapsible ? 'pt-3 border-t border-white/[0.06]' : undefined}>
              {category.collapsible ? (
                <button
                  onClick={() => setExtrasOpen(o => !o)}
                  className="w-full flex items-center justify-between px-3 mb-1.5 text-[10px] font-semibold text-white/25 hover:text-white/50 uppercase tracking-widest transition-colors"
                >
                  {category.title}
                  <ChevronRight size={12} className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
                </button>
              ) : (
                <p className="px-3 text-[10px] font-semibold text-white/25 uppercase tracking-widest mb-1.5">
                  {category.title}
                </p>
              )}
              {expanded && (
              <div className="space-y-0.5">
                {category.items.map((tool) => {
                  const isActive = activeTool === tool.id;
                  const Icon = tool.icon;

                  return (
                    <motion.button
                      key={tool.id}
                      onClick={() => {
                        onSelectTool(tool.id);
                        if (window.innerWidth < 1024) onClose();
                      }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                        transition-all duration-200 group relative overflow-hidden
                        ${isActive ? 'text-white' : 'text-white/45 hover:text-white/80'}
                      `}
                      style={isActive ? {
                        background: `linear-gradient(135deg, ${tool.glow.replace('0.25', '0.18')} 0%, rgba(255,255,255,0.06) 100%)`,
                        boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 2px 8px ${tool.glow}`,
                      } : undefined}
                    >
                      {!isActive && (
                        <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.04] transition-colors duration-200" />
                      )}
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200"
                        style={{
                          backgroundColor: isActive ? tool.color + '22' : 'rgba(255,255,255,0.05)',
                          color: isActive ? tool.color : 'inherit',
                        }}
                      >
                        <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                      </div>
                      <span className="text-[13px] font-medium flex-1 text-left leading-none">
                        {tool.label}
                      </span>
                      {isActive && (
                        <ChevronRight size={12} className="text-white/40 shrink-0" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </nav>

        {/* Admin-only: usage & credits report */}
        {user.role === 'admin' && (
          <div className="px-3 pb-1">
            <motion.button
              onClick={() => onSelectTool('usage-report')}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden"
              style={{ color: activeTool === 'usage-report' ? '#e4c184' : 'rgba(255,255,255,0.45)' }}
            >
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.04] transition-colors duration-200" />
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200"
                style={{
                  backgroundColor: activeTool === 'usage-report' ? 'rgba(228,193,132,0.15)' : 'rgba(255,255,255,0.05)',
                }}
              >
                <Coins size={15} strokeWidth={activeTool === 'usage-report' ? 2.2 : 1.8} />
              </div>
              <span className="text-[13px] font-medium flex-1 text-left leading-none">Usage &amp; Credits</span>
            </motion.button>
          </div>
        )}

        {/* History button */}
        <div className="px-3 pb-2">
          <motion.button
            onClick={onHistoryOpen}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden text-white/45 hover:text-white/80"
          >
            <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.04] transition-colors duration-200" />
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              <Clock size={15} strokeWidth={1.8} />
            </div>
            <span className="text-[13px] font-medium flex-1 text-left leading-none">History</span>
          </motion.button>
        </div>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04]">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: '#54c4b6', boxShadow: '0 0 6px rgba(84,196,182,0.8)' }} />
            <span className="text-[11px] text-white/35 font-medium flex-1 truncate">{user.username}</span>
            <button
              onClick={onLogout}
              title="Sign out"
              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/35 hover:text-white/70 shrink-0"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
